import { supabase } from "./supabase";
import type { Resposta } from "./adminCatalogo";

/**
 * Os DOCUMENTOS do produto (000306 — o G5 do Plano de Melhorias).
 *
 * ZERO regra aqui. A matriz (quais documentos existem, se são obrigatórios, o
 * vínculo), a versão, o motivo e a pasta do arquivo são decididos no banco, e
 * as recusas trazem a frase pronta — traduzir `motivo` aqui criaria uma segunda
 * tabela de frases.
 *
 * O arquivo SOBE primeiro e só depois é registrado: o banco confere que ele
 * está no bucket antes de aceitar a linha. Se o banco recusar, a tela desfaz o
 * upload — e CONFERE que desfez, listando a pasta, porque `remove()` responde
 * 200 mesmo quando a política não deixa apagar nada (a cicatriz da 000285).
 */

export const BUCKET = "documentos-de-produto";

export type TipoDeDocumento = {
  id: string;
  nome: string;
  categoria: string;
  /** Nulo = "a definir": o time ainda não decidiu. Nunca vira "não". */
  obrigatorio: boolean | null;
  vinculo: "produto" | "turma" | "ambos" | null;
  versionado: boolean | null;
  ordem: number;
  ativo: boolean;
};

export type DocumentoDoProduto = {
  id: string;
  tipo_id: string;
  turma_id: string | null;
  turma_numero: number | null;
  titulo: string;
  arquivo_path: string;
  nome_arquivo: string;
  mime: string | null;
  tamanho_bytes: number | null;
  versao: number;
  vigente: boolean;
  motivo: string | null;
  enviado_por: string;
  enviado_em: string;
  retirado_em: string | null;
  retirado_por: string | null;
  motivo_retirada: string | null;
};

export type DocumentosDoProduto = {
  ok: true;
  /** Os tipos da LISTA: os ativos e o desativado que ainda tem documento deste curso. */
  tipos: TipoDeDocumento[];
  /**
   * A MATRIZ inteira (000307), para o editor — o tipo desativado sem documento
   * também, senão ele não teria como ser reativado.
   */
  matriz: TipoDeDocumento[];
  turmas: { id: string; numero: number }[];
  documentos: DocumentoDoProduto[];
  /** Os OBRIGATÓRIOS sem versão vigente. "A definir" não entra. */
  faltando: string[];
};

export async function lerDocumentosDoProduto(
  curso: string,
): Promise<DocumentosDoProduto | (Resposta & { ok: false })> {
  const { data, error } = await supabase.rpc("cat_admin_documentos_do_produto", { p_curso_slug: curso });
  if (error) return { ok: false, detalhe: error.message };
  return data as DocumentosDoProduto | (Resposta & { ok: false });
}

/** A extensão do arquivo, só letras e números — o NOME fica guardado à parte. */
function extensaoDe(nome: string): string {
  const m = /\.([a-z0-9]{1,8})$/i.exec(nome);
  return m ? `.${m[1].toLowerCase()}` : "";
}

/**
 * Envia um documento: sobe o arquivo na pasta do curso e registra a versão.
 * O caminho é um uuid (o nome que o operador deu ao arquivo pode ter qualquer
 * caractere, e ele vai guardado em `nome_arquivo`).
 */
export async function enviarDocumento(args: {
  curso: string;
  tipoId: string;
  turmaId: string | null;
  titulo: string;
  arquivo: File;
  motivo: string | null;
}): Promise<Resposta> {
  const caminho = `${args.curso}/${crypto.randomUUID()}${extensaoDe(args.arquivo.name)}`;
  const subida = await supabase.storage.from(BUCKET).upload(caminho, args.arquivo, {
    contentType: args.arquivo.type || undefined,
    upsert: false,
  });
  if (subida.error) {
    return { ok: false, detalhe: `o arquivo não subiu: ${subida.error.message}` };
  }

  const { data, error } = await supabase.rpc("cat_admin_registrar_documento", {
    p_curso_slug: args.curso,
    p_tipo_id: args.tipoId,
    p_turma_id: args.turmaId,
    p_titulo: args.titulo,
    p_arquivo_path: caminho,
    p_nome_arquivo: args.arquivo.name,
    p_mime: args.arquivo.type || null,
    p_tamanho: args.arquivo.size,
    p_motivo: args.motivo,
  });
  const r: Resposta = error ? { ok: false, detalhe: error.message } : (data as Resposta);
  if (!r.ok) {
    // O banco recusou: o arquivo SEM linha não pode ficar no bucket. A política
    // de exclusão só deixa apagar o que nenhum documento referencia — e é
    // exatamente este. A conferência vem depois, pela listagem.
    await supabase.storage.from(BUCKET).remove([caminho]);
    const nome = caminho.slice(args.curso.length + 1);
    const sobrou = await supabase.storage.from(BUCKET).list(args.curso, { search: nome });
    if ((sobrou.data ?? []).some((o) => o.name === nome)) {
      return {
        ...r,
        detalhe: `${r.detalhe ?? "o banco recusou"} — e o arquivo enviado ficou no bucket sem documento; avise a equipe (${caminho}).`,
      };
    }
  }
  return r;
}

export async function retirarDocumento(id: string, motivo: string): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_retirar_documento", { p_id: id, p_motivo: motivo });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/**
 * Cria (sem `id`) ou edita um documento da MATRIZ. A linha vai INTEIRA: aqui
 * nulo quer dizer "a definir", e não "não mexe" como no resto do catálogo.
 */
export async function salvarTipoDeDocumento(t: {
  id: string | null;
  nome: string;
  categoria: string;
  obrigatorio: boolean | null;
  vinculo: TipoDeDocumento["vinculo"];
  versionado: boolean | null;
  ordem: number;
  ativo: boolean;
  motivo: string | null;
}): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_salvar_tipo_de_documento", {
    p_id: t.id,
    p_nome: t.nome,
    p_categoria: t.categoria,
    p_obrigatorio: t.obrigatorio,
    p_vinculo: t.vinculo,
    p_versionado: t.versionado,
    p_ordem: t.ordem,
    p_ativo: t.ativo,
    p_motivo: t.motivo,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/**
 * O link de download, assinado por um minuto — o bucket é privado, e um link
 * que durasse mais seria um link que alguém guarda e repassa.
 */
export async function linkDoDocumento(caminho: string, nomeParaBaixar?: string): Promise<string | null> {
  // Com o NOME original: o caminho no bucket é um uuid, e quem baixa quer o arquivo
  // com o nome que ele tinha.
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(caminho, 60, nomeParaBaixar ? { download: nomeParaBaixar } : undefined);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}

/** Uma linha do HISTÓRICO do produto: a página, os documentos e os SKUs. */
export type MudancaDoProduto = {
  id: number;
  em: string;
  alvo: string;
  acao: string;
  /** A frase do BANCO (`cat_resumo_da_mudanca`). */
  resumo: string;
  por: string;
  motivo: string | null;
};

export async function lerHistoricoDoProduto(
  curso: string,
): Promise<Resposta & { total?: number; mudancas?: MudancaDoProduto[] }> {
  const { data, error } = await supabase.rpc("cat_admin_historico_do_produto", { p_curso_slug: curso });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta & { total?: number; mudancas?: MudancaDoProduto[] };
}
